import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs'
import {
  catchError,
  filter,
  map,
  switchMap,
  take,
  tap,
  timeout,
  withLatestFrom
} from 'rxjs/operators'
import { AppState } from 'src/app/core/core.state'
import { NotificationService } from 'src/app/core/notification.service'

import { Component, OnInit } from '@angular/core'
import { UntypedFormGroup } from '@angular/forms'
import { Actions, ofType } from '@ngrx/effects'
import { Store } from '@ngrx/store'

import { Sale } from '../sale'
import { saleActions } from '../sale.actions'
import { saleSelectors } from '../sale.selectors'
import { userRoleSelectors } from '../../role/user-role/user-role.selectors'
import { RolesType } from '../../role/role'
import { environment } from 'src/environments/environment'
import { CondOperator, RequestQueryBuilder } from '@nestjsx/crud-request'
import { SaleService } from '../sale.service'
import { paymentActions } from '../payment/payment.actions'
import { paymentSelectors } from '../payment/payment.selectors'
import {
  CollectStatusType,
  Payment,
  PaymentProviderType,
  PayMethodType
} from '../payment/payment'
import { OperationService } from '../../operation/operation.service'

@Component({
  selector: 'sale-general-main-tab',
  templateUrl: './sale-general-tab.component.html',
  styleUrls: ['./sale-general-tab.component.scss']
})
export class SaleGeneralMainTabComponent implements OnInit {
  public sale$: Observable<Sale>
  public saleId$: Observable<number>
  public form: UntypedFormGroup
  public moduleConfigs = environment.moduleConfigs
  public refundableAmount$: Observable<number>
  public canDoPspRefund$: Observable<boolean>
  public pspPartialRefundAwaitingCapture$: Observable<boolean>
  public pendingResolveOperationId$ = new BehaviorSubject<number | null>(null)

  constructor(
    private actions$: Actions,
    private notificationService: NotificationService,
    private saleService: SaleService,
    private operationService: OperationService,
    private store: Store<AppState>
  ) {}

  ngOnInit() {
    this.saleService.loadedSaleInStore = false
    this.saleId$ = this.store.select(saleSelectors.selectIdRouteParam)

    this.saleId$
      .pipe(
        take(1),
        filter((saleId) => Boolean(saleId))
      )
      .subscribe((saleId) => {
        if (saleId) {
          this.fetchSale(saleId)
          this.refreshPendingResolveOperation(saleId)
        }
      })

    this.sale$ = this.saleId$.pipe(
      switchMap((saleId) => this.store.select(saleSelectors.selectById, saleId))
    )

    this.refundableAmount$ = this.sale$.pipe(
      filter((sale) => Boolean(sale)),
      withLatestFrom(this.store.select(paymentSelectors.selectEntities)),
      map(([sale, paymentEntities]) =>
        this.getRefundableAmount(sale, paymentEntities)
      )
    )

    this.pspPartialRefundAwaitingCapture$ = combineLatest([
      this.sale$,
      this.store.select(saleSelectors.selectEntities),
      this.store.select(paymentSelectors.selectEntities)
    ]).pipe(
      filter(([sale, _saleEntities, _paymentEntities]) => Boolean(sale)),
      map(([sale, saleEntities, paymentEntities]) => {
        const originalSale = saleEntities[sale.isCreditOfSaleId]

        if (!originalSale) {
          return false
        }

        const refundAmount = this.getRefundableAmount(sale, paymentEntities)

        if (refundAmount <= 0) {
          return false
        }

        const pspPayments: Payment[] = (originalSale.payments ?? [])
          .map((pId: number) => paymentEntities[pId])
          .filter(
            (p: Payment) =>
              p?.externalPaymentId &&
              p.collectStatus === CollectStatusType.COLLECT_STATUS_COLLECTED
          )

        // mirrors the refund distribution of the backend refund action where
        // every psp payment is refunded with min(pending, payment amount); a
        // slice smaller than the payment amount is a partial refund which
        // Adyen only accepts once the payment has been captured
        let pendingAmountCents = Math.round(refundAmount * 100)
        for (const pspPayment of pspPayments) {
          const paymentAmountCents = Math.round(Number(pspPayment.amount) * 100)
          const sliceCents = Math.min(pendingAmountCents, paymentAmountCents)

          if (
            pspPayment.paymentProviderType === PaymentProviderType.Adyen &&
            sliceCents < paymentAmountCents &&
            !pspPayment.capturedAt
          ) {
            return true
          }

          pendingAmountCents -= sliceCents
          if (pendingAmountCents <= 0) {
            break
          }
        }

        return false
      })
    )

    this.canDoPspRefund$ = combineLatest([
      this.sale$,
      this.store.select(saleSelectors.selectEntities),
      this.store.select(paymentSelectors.selectEntities)
    ]).pipe(
      filter(([sale, _saleEntities, _paymentEntities]) => Boolean(sale)),
      map(([sale, saleEntities, paymentEntities]) => {
        const originalSale = saleEntities[sale.isCreditOfSaleId]

        if (!originalSale) {
          return false
        }
        const originalSalePayments: Payment[] = originalSale.payments?.map(
          (pId) => paymentEntities[pId]
        )

        if (!originalSalePayments?.length) {
          return false
        }
        // check if original sale was not payed with balance or in cash
        if (
          !originalSalePayments.find((p) =>
            [
              PayMethodType.PAY_METHOD_CASH,
              PayMethodType.PAY_METHOD_ACCOUNT
            ].includes(p.payMethod)
          ) &&
          originalSalePayments.find((p) => p.externalPaymentId)
        ) {
          return true
        }

        if (
          originalSalePayments.find((p) =>
            [PaymentProviderType.Adyen, PaymentProviderType.Mollie].includes(
              p.paymentProviderType
            )
          )
        ) {
          return true
        }

        if (
          originalSalePayments.find((p) =>
            [PaymentProviderType.Worldline].includes(p.paymentProviderType)
          )
        ) {
          if (
            originalSalePayments.find(
              (p) => p.payMethod === PayMethodType.PAY_METHOD_DIRECT_DEBIT
            )
          ) {
            return false // worldline cannot do refunds on direct debit paymethod
          } else {
            return true
          }
        }

        return false
      })
    )
  }

  public onClickRefund(refundType: 'balance' | 'alreadyRefunded' | 'psp') {
    return this.sale$.pipe(take(1)).subscribe((sale) => {
      return this.saleService
        .refund(sale.id, refundType)
        .pipe(take(1))
        .subscribe((reponse) => {
          if (reponse.message) {
            this.notificationService.emit(reponse.message, 'warning')
          }
          if (reponse.success) {
            this.notificationService.emit('alerts.success', 'success')
            this.store
              .select(paymentSelectors.selectByFilter, {
                field: 'saleId',
                value: sale.id
              })
              .pipe(take(1))
              .subscribe((payments) => {
                if (payments.length) {
                  this.store.dispatch(
                    paymentActions.removeMany({
                      ids: payments.map((payment) => payment.id)
                    })
                  )
                }
                this.fetchSale(sale.id)
              })
          }
        })
    })
  }

  public onAcceptAndClose(operationId: number): void {
    this.operationService
      .operate(operationId, {})
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.notificationService.emit('alerts.success', 'success')
          this.pendingResolveOperationId$.next(null)
        },
        error: () =>
          this.notificationService.emit('alerts.operationNotMade', 'error')
      })
  }

  private refreshPendingResolveOperation(saleId: number): void {
    this.saleService
      .getPendingResolveUndecidedOperationId(saleId)
      .pipe(take(1))
      .subscribe((operationId) =>
        this.pendingResolveOperationId$.next(operationId)
      )
  }

  public onSubmit(form): void {
    this.saleId$.pipe(take(1)).subscribe((saleId) =>
      this.store.dispatch(
        saleActions.updateOneById({
          id: saleId,
          params: {
            ...form,
            id: parseInt(saleId.toString())
          }
        })
      )
    )
    this.actions$
      .pipe(
        ofType(saleActions.upsertMany),
        take(1),
        timeout(1000),
        catchError((error) => of(null))
      )
      .subscribe((action) =>
        action
          ? this.notificationService.emit('alerts.success', 'success')
          : null
      )
  }

  public hasRequiredRole$(roles: RolesType[]): Observable<boolean> {
    if (!roles?.length) {
      return of(true)
    }
    return this.store.select(userRoleSelectors.selectUserHasRoles, roles)
  }

  private getRefundableAmount(
    sale: Sale,
    paymentEntities: { [id: number]: Payment }
  ): number {
    const salePayments: Payment[] =
      sale.payments?.map((pId: number) => paymentEntities[pId]) ?? []

    const undecidedPayment = salePayments.find(
      (p) =>
        p?.payMethod === PayMethodType.PAY_METHOD_UNDECIDED &&
        Number(p.amount) < 0
    )

    if (!undecidedPayment) {
      return 0
    }

    return Number(undecidedPayment.amount) * -1
  }

  private fetchSale(saleId: number) {
    this.store.dispatch(
      saleActions.fetchMany({
        params: RequestQueryBuilder.create({
          search: {
            $or: [
              { isCreditOfSaleId: saleId },
              {
                id: {
                  [CondOperator.IN]: [saleId]
                }
              }
            ]
          },
          join: [
            { field: 'creditSales' },
            { field: 'organization' },
            { field: 'customer' },
            { field: 'payments' }
          ]
        })
      })
    )
  }
}
